import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import NewsletterModal from '../home/components/NewsletterModal';
import CampaignMiniBanner from '../../components/feature/CampaignMiniBanner';

const chairData: Record<string, {
  name: string;
  tagline: string;
  description: string;
  image: string;
  features: { title: string; desc: string; hasLink?: boolean }[];
  specs: { label: string; value: string }[];
  price: string;
  priceGray: string;
  status: string;
  purchaseLink: string | null;
  comingSoon?: boolean;
  headrestOption?: {
    name: string;
    priceBlack: string;
    priceGray: string;
    image: string;
    description: string;
  } | null;
}> = {
  pro: {
    name: 'MeVER Chair Pro v1.1',
    tagline: 'プロフェッショナルのための究極の座り心地',
    description:
      '長時間のクリエイティブワークに最適化された、フルアジャスタブル設計のフラッグシップモデル。6Dアームレスト、4Dヘッドレスト、15ヶ所のフルアジャスト機能、調整式ランバーサポートなど、あらゆる体型と姿勢に対応します。電動昇降デスクとの相性も抜群。',
    image:
      'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/18555a65f21397adfed78720e8be08d7.png',
    features: [
      {
        title: '4Dヘッドレスト',
        desc: '上下・前後・角度・回転の4方向調整で、首と頭を完璧にサポート',
      },
      {
        title: '6Dアームレスト',
        desc: '高さ・前後・左右・角度・幅・回転の6方向調整で、肩への負担を軽減',
      },
      {
        title: 'アダプティブランバー',
        desc: '腰椎の自然なカーブに沿って、上下前後の調整が可能な2Dランバーサポート',
      },
      { title: 'メッシュバック', desc: '通気性に優れた高密度メッシュで、長時間でも快適' },
      {
        title: 'シンクロ135°リクライニング',
        desc: '体の動きに合わせてアームレストと背もたれが連動して135°までリクライニング',
      },
      { title: '高耐久キャスター', desc: 'フローリングにも優しい静音設計' },
    ],
    specs: [
      { label: '耐荷重', value: '150kg' },
      { label: '座面高', value: '42-52cm' },
      { label: 'リクライニング角度', value: '90-135°' },
      { label: '本体重量', value: '22kg' },
      { label: '保証期間', value: '5年' },
    ],
    price: '¥123,200',
    priceGray: '¥128,700',
    status: '2026年発売予定',
    purchaseLink: 'https://mever.design/products/mever-chair-pro',
    comingSoon: false,
    headrestOption: null,
  },
  core: {
    name: 'MeVER Chair Core',
    tagline: '必要十分の人間工学設計',
    description:
      'エッセンシャルな機能に絞り込んだ、ミニマルデザインのスタンダードモデル。シンプルながらもPro譲りの前後調整機能のついたランバーサポートと座面や4Dアームレストを搭載。人間工学に基づいた設計で、快適な座り心地を実現します。',
    image:
      'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/f4a7d90296f789d46c6753357c7a51a3.png',
    features: [
      { title: '4Dヘッドレスト（別売りオプション）', desc: '上下・前後・角度・回転の4方向調整で、首と頭をしっかりサポート', hasLink: true },
      { title: '4Dアームレスト', desc: '高さ・前後・角度の3方向調整', hasLink: false },
      { title: 'ランバーサポート', desc: '前後上下の調整機能を持った、腰をしっかりサポートする調整式ランバーサポート', hasLink: false },
      { title: 'メッシュバック', desc: '通気性の良いメッシュ素材を採用', hasLink: false },
      { title: 'シートスライド', desc: '座面の奥行きを調整可能', hasLink: false },
      { title: '静音キャスター', desc: 'スムーズな移動を実現', hasLink: false },
    ],
    specs: [
      { label: '耐荷重', value: '120kg' },
      { label: '座面高', value: '43-51cm' },
      { label: 'リクライニング角度', value: '90-120°' },
      { label: '本体重量', value: '18kg' },
      { label: '保証期間', value: '3年' },
    ],
    price: '¥79,200',
    priceGray: '¥84,700',
    status: '発売準備中',
    purchaseLink: null,
    comingSoon: true,
    headrestOption: {
      name: '4Dヘッドレスト',
      priceBlack: '¥4,950',
      priceGray: '¥6,050',
      image: 'https://public.readdy.ai/ai/img_res/aa32e8430d9f5be45809fbd71d112213.jpg',
      description: '上下・前後・角度・回転の4方向調整が可能な高機能ヘッドレスト。長時間のデスクワークでも首と頭をしっかりサポートし、疲労を軽減します。',
    },
  },
  style: {
    name: 'MeVER Chair Style',
    tagline: '空間と調和するデザイン',
    description:
      'インテリアに溶け込む洗練されたデザインと、快適性を両立したライフスタイルモデル。リビングやホームオフィスに最適です。',
    image:
      'https://static.readdy.ai/image/05cd76bb9b80c0f39a77a9e694effa95/0e5886a34ba8b9935f350bf408063f8e.png',
    features: [
      { title: 'ミニマルモダンデザイン', desc: 'どんなインテリアにも合うミニマルで軽やかなシルエット' },
      { title: 'ファブリックシート', desc: '上質なファブリック素材で心地よい座り心地' },
      { title: '3Dアームレスト', desc: '上下/前後/回転調整が可能な3Dアームレスト' },
      { title: 'バランスボール機能', desc: '程よい硬さのモールドウレタン' },
      { title: 'ロッキング機能', desc: 'リラックスできるロッキング機構' },
      { title: 'デザインキャスター', desc: 'インテリアに馴染むデザイン' },
    ],
    specs: [
      { label: '耐荷重', value: '100kg' },
      { label: '座面高', value: '44-50cm' },
      { label: 'リクライニング角度', value: '90-110°' },
      { label: '本体重量', value: '14kg' },
      { label: '保証期間', value: '2年' },
    ],
    price: '¥69,300',
    priceGray: '¥71,500',
    status: '2026年発売予定',
    purchaseLink: 'https://mever.design/products/mever-chair-style',
    comingSoon: false,
    headrestOption: null,
  },
};

export default function ChairDetail() {
  const { model } = useParams();
  const [scrolled, setScrolled] = useState(false);
  const [showCoreModal, setShowCoreModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const chair = chairData?.[model as string] ?? null;

  if (!chair) {
    return (
      <div className="min-h-screen flex items-center justify-center" role="alert">
        <p>製品が見つかりません</p>
      </div>
    );
  }

  return (
    <div className="relative">
      <Navbar scrolled={scrolled} />
      <div className="pt-16 sm:pt-20">
        <CampaignMiniBanner />
      </div>

      {/* Hero */}
      <section className="pt-8 sm:pt-12 pb-12 sm:pb-20 bg-black" aria-labelledby="chair-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <Link
            to="/"
            className="inline-flex items-center text-white/60 hover:text-white mb-6 sm:mb-8 transition-colors cursor-pointer tracking-wide text-sm sm:text-base"
          >
            <i className="ri-arrow-left-line mr-2" aria-hidden="true"></i>
            トップに戻る
          </Link>
          
          {/* Price Revision Notice - Only for Pro and Style */}
          {model !== 'core' && (
            <Link
              to="/campaign"
              className="block mb-6 sm:mb-8 cursor-pointer group"
              aria-label="価格改定のお知らせ - キャンペーンページへ"
            >
              <div className="bg-gradient-to-r from-red-900/40 via-red-800/40 to-red-900/40 border border-red-700/30 rounded-lg sm:rounded-xl p-4 sm:p-5 backdrop-blur-sm hover:border-red-600/50 transition-all">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 flex-1">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1 bg-red-500 text-white text-xs font-medium tracking-wide rounded-md">
                        <i className="ri-error-warning-line text-sm" aria-hidden="true"></i>
                        重要
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm sm:text-base font-light tracking-wide mb-0.5">
                        2026年4月1日より価格改定
                      </p>
                      <p className="text-white/70 text-xs sm:text-sm font-extralight tracking-wide">
                        3/31まで価格維持予約受付中 + Amazonギフト券5,000円分プレゼント
                      </p>
                    </div>
                  </div>
                  <i className="ri-arrow-right-line text-white/60 group-hover:text-white group-hover:translate-x-1 transition-all text-lg flex-shrink-0" aria-hidden="true"></i>
                </div>
              </div>
            </Link>
          )}

          <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-16 items-center">
            <div className="order-2 lg:order-1">
              <div className="flex flex-wrap items-center gap-3 mb-4 sm:mb-6">
                <span className="inline-block px-3 sm:px-4 py-1.5 sm:py-2 bg-[#8E7AB5]/20 text-[#8E7AB5] text-xs sm:text-sm tracking-[0.1em] rounded-full">
                  {chair.status}
                </span>
                {chair.comingSoon && (
                  <span className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-amber-500/20 text-amber-400 text-xs sm:text-sm tracking-[0.1em] rounded-full">
                    <i className="ri-time-line text-sm" aria-hidden="true"></i>
                    発売準備中
                  </span>
                )}
                {model === 'core' && (
                  <span className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 text-white/50 text-xs sm:text-sm tracking-[0.1em] rounded-full border border-white/15">
                    <i className="ri-close-circle-line text-sm" aria-hidden="true"></i>
                    キャンペーン対象外
                  </span>
                )}
              </div>
              <h1 id="chair-title" className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extralight text-white mb-4 sm:mb-6 tracking-[0.05em] sm:tracking-[0.1em]">
                {chair.name}
              </h1>
              <p className="text-lg sm:text-xl md:text-2xl font-extralight text-white/80 mb-4 sm:mb-6 tracking-wide">
                {chair.tagline}
              </p>
              <p className="text-sm sm:text-base md:text-lg font-extralight text-white/60 leading-relaxed mb-6 sm:mb-8 tracking-wide">
                {chair.description}
              </p>
              <div className="mb-6 sm:mb-8">
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-xs sm:text-sm text-white/60">Black</span>
                  <span className="text-2xl sm:text-3xl md:text-4xl font-extralight text-white tracking-wide">
                    {chair.price}
                  </span>
                  <span className="text-sm sm:text-base md:text-lg text-white/60">(税込)</span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="text-xs sm:text-sm text-white/60">Gray</span>
                  <span className="text-2xl sm:text-3xl md:text-4xl font-extralight text-white tracking-wide">
                    {chair.priceGray}
                  </span>
                  <span className="text-sm sm:text-base md:text-lg text-white/60">(税込)</span>
                </div>
              </div>
              {chair.comingSoon ? (
                <button
                  onClick={() => setShowCoreModal(true)}
                  className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-white text-black text-sm sm:text-base font-light tracking-[0.1em] hover:bg-white/90 transition-colors whitespace-nowrap rounded-md cursor-pointer"
                  aria-haspopup="dialog"
                >
                  <i className="ri-mail-line mr-2" aria-hidden="true"></i>
                  発売に関する情報を受け取る
                </button>
              ) : chair.purchaseLink ? (
                <a
                  href={chair.purchaseLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full sm:w-auto text-center px-8 sm:px-10 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm sm:text-base font-light tracking-[0.1em] hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
                  aria-label={`${chair.name}を購入する（外部サイト）`}
                >
                  購入する
                </a>
              ) : (
                <button
                  disabled
                  className="w-full sm:w-auto px-8 sm:px-10 py-3 sm:py-4 bg-white/20 text-white/60 text-sm sm:text-base font-light tracking-[0.1em] whitespace-nowrap rounded-md cursor-not-allowed"
                  aria-disabled="true"
                >
                  準備中
                </button>
              )}
            </div>
            <div className="aspect-square bg-white/5 rounded-xl sm:rounded-2xl overflow-hidden order-1 lg:order-2">
              <img
                src={chair.image}
                alt={`${chair.name} - ${chair.tagline}`}
                className="w-full h-full object-cover object-center"
                loading="eager"
                width="600"
                height="600"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Headrest Option - Core only */}
      {chair.headrestOption && (
        <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-black to-gray-900" aria-labelledby="headrest-option-heading">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-10 sm:mb-14">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#8E7AB5]/20 text-[#8E7AB5] text-xs sm:text-sm font-light tracking-[0.1em] rounded-full mb-4 sm:mb-6">
                <i className="ri-add-circle-line text-sm" aria-hidden="true"></i>
                別売りオプション
              </span>
              <h2 id="headrest-option-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-white mb-3 tracking-[0.1em]">
                {chair.headrestOption.name}
              </h2>
              <p className="text-sm sm:text-base text-white/50 font-extralight tracking-wide max-w-2xl mx-auto">
                {chair.headrestOption.description}
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-10 sm:gap-16 items-center">
              <div className="aspect-square bg-white/5 rounded-xl sm:rounded-2xl overflow-hidden">
                <img
                  src={chair.headrestOption.image}
                  alt={`${chair.headrestOption.name} - Core専用オプション`}
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                  width="600"
                  height="600"
                />
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-light text-white mb-4 tracking-wide">
                  Core専用 {chair.headrestOption.name}
                </h3>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start gap-3 text-white/70 text-sm sm:text-base font-extralight">
                    <i className="ri-arrow-up-down-line text-[#8E7AB5] mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                    上下調整 — 高さを自由に調整可能
                  </li>
                  <li className="flex items-start gap-3 text-white/70 text-sm sm:text-base font-extralight">
                    <i className="ri-arrow-left-right-line text-[#8E7AB5] mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                    前後調整 — 奥行きを調整可能
                  </li>
                  <li className="flex items-start gap-3 text-white/70 text-sm sm:text-base font-extralight">
                    <i className="ri-rotate-lock-line text-[#8E7AB5] mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                    角度調整 — 前後の角度を調整
                  </li>
                  <li className="flex items-start gap-3 text-white/70 text-sm sm:text-base font-extralight">
                    <i className="ri-refresh-line text-[#8E7AB5] mt-0.5 flex-shrink-0" aria-hidden="true"></i>
                    回転調整 — 左右の回転が可能
                  </li>
                </ul>
                <div className="mb-8">
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-xs sm:text-sm text-white/50">Black</span>
                    <span className="text-2xl sm:text-3xl font-extralight text-white">{chair.headrestOption.priceBlack}</span>
                    <span className="text-sm text-white/50">(税込)</span>
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-xs sm:text-sm text-white/50">Gray</span>
                    <span className="text-2xl sm:text-3xl font-extralight text-white">{chair.headrestOption.priceGray}</span>
                    <span className="text-sm text-white/50">(税込)</span>
                  </div>
                </div>
                <Link
                  to="/accessories"
                  className="inline-flex items-center gap-2 px-8 py-3 sm:py-4 bg-[#8E7AB5] text-white text-sm font-light tracking-[0.1em] hover:bg-[#7A6A9E] transition-colors whitespace-nowrap rounded-md cursor-pointer"
                >
                  オプション詳細を見る
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-16 sm:py-20 md:py-24 bg-white" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 id="features-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-10 sm:mb-12 md:mb-16 text-center tracking-[0.1em] sm:tracking-[0.15em]">
            主な特徴
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8" role="list">
            {chair.features.map((feature, index) => (
              <article key={index} className="p-5 sm:p-6 md:p-8 bg-gray-50 rounded-lg sm:rounded-xl" role="listitem">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-[#8E7AB5]/10 rounded-lg mb-3 sm:mb-4">
                  <i className="ri-checkbox-circle-line text-xl sm:text-2xl text-[#8E7AB5]" aria-hidden="true"></i>
                </div>
                <h3 className="text-lg sm:text-xl font-light text-black mb-1.5 sm:mb-2 tracking-wide">
                  {feature.title}
                </h3>
                <p className="text-sm sm:text-base text-black/60 font-extralight tracking-wide">{feature.desc}</p>
                {feature.hasLink && (
                  <Link
                    to="/accessories"
                    className="inline-flex items-center mt-3 text-sm text-[#8E7AB5] hover:text-[#7A6A9E] transition-colors cursor-pointer"
                  >
                    オプション詳細を見る
                    <i className="ri-arrow-right-line ml-1" aria-hidden="true"></i>
                  </Link>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Specs */}
      <section className="py-16 sm:py-20 md:py-24 bg-gray-50" aria-labelledby="specs-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 id="specs-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-10 sm:mb-12 md:mb-16 text-center tracking-[0.1em] sm:tracking-[0.15em]">
            スペック
          </h2>
          <div className="bg-white rounded-lg sm:rounded-xl overflow-hidden">
            <dl>
              {chair.specs.map((spec, index) => (
                <div
                  key={index}
                  className={`flex justify-between items-center px-5 sm:px-8 py-4 sm:py-5 ${
                    index !== chair.specs.length - 1 ? 'border-b border-gray-100' : ''
                  }`}
                >
                  <dt className="text-sm sm:text-base text-black/60 font-extralight tracking-wide">{spec.label}</dt>
                  <dd className="text-sm sm:text-base text-black font-light tracking-wide">{spec.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </section>

      {/* Campaign Section */}
      {model !== 'core' ? (
      <section
        className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-[#FFF5F5] via-[#FFF8F0] to-[#FFFAF5]"
        aria-labelledby="campaign-heading"
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-8 sm:mb-10 md:mb-12">
            <span className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-[#E8B4B8]/20 text-[#C4787A] text-xs sm:text-sm font-light tracking-[0.1em] rounded-full mb-4 sm:mb-6">
              <span className="text-base sm:text-lg" aria-hidden="true">
                🌸
              </span>
              期間限定キャンペーン
            </span>
            <h2 id="campaign-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight text-black mb-3 sm:mb-4 tracking-[0.1em] sm:tracking-[0.15em]">
              新生活応援キャンペーン
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-black/60 font-extralight tracking-wide">
              MeVER Chairをお得にお求めいただけるチャンスです
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-8 sm:mb-10 md:mb-12">
            {/* 特典1 */}
            <article className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 shadow-sm border border-[#E8B4B8]/20">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-[#E8B4B8]/10 rounded-lg sm:rounded-xl mb-4 sm:mb-6">
                <i className="ri-price-tag-3-line text-xl sm:text-2xl text-[#C4787A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-light text-black mb-2 sm:mb-3 tracking-wide">
                値上げ前価格で購入
              </h3>
              <p className="text-sm sm:text-base text-black/60 font-extralight leading-relaxed mb-3 sm:mb-4 tracking-wide">
                3月1日より価格改定を予定しておりますが、<strong className="text-black font-light">3月30日まで</strong>
                のご購入は現行価格のままお求めいただけます。
              </p>
              <div className="flex items-center gap-2 text-[#C4787A] text-sm sm:text-base">
                <i className="ri-calendar-check-line text-base sm:text-lg" aria-hidden="true"></i>
                <span className="font-light tracking-wide">〜2025年3月30日まで</span>
              </div>
            </article>

            {/* 特典2 */}
            <article className="bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 shadow-sm border border-[#E8B4B8]/20">
              <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-[#E8B4B8]/10 rounded-lg sm:rounded-xl mb-4 sm:mb-6">
                <i className="ri-gift-line text-xl sm:text-2xl text-[#C4787A]" aria-hidden="true"></i>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-light text-black mb-2 sm:mb-3 tracking-wide">
                予約特典
              </h3>
              <p className="text-sm sm:text-base text-black/60 font-extralight leading-relaxed mb-3 sm:mb-4 tracking-wide">
                在庫切れの場合でも、3月30日までにご予約（先入金）いただいたお客様には特別特典をご用意しております。
              </p>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 sm:gap-3">
                  <span
                    className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-[#C4787A] text-white text-xs sm:text-sm font-light rounded-full"
                    aria-hidden="true"
                  >
                    1
                  </span>
                  <span className="text-sm sm:text-base text-black font-light tracking-wide">10% OFF</span>
                </li>
                <li className="flex items-center gap-2 sm:gap-3">
                  <span
                    className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center bg-[#C4787A] text-white text-xs sm:text-sm font-light rounded-full"
                    aria-hidden="true"
                  >
                    2
                  </span>
                  <span className="text-sm sm:text-base text-black font-light tracking-wide">
                    Amazonギフト券 5,000円分
                  </span>
                </li>
              </ul>
            </article>
          </div>

          {/* 注意事項 */}
          <aside className="bg-white/60 rounded-lg sm:rounded-xl p-4 sm:p-6 border border-[#E8B4B8]/10">
            <h4 className="text-xs sm:text-sm font-light text-black/80 mb-2 sm:mb-3 flex items-center gap-2 tracking-wide">
              <i className="ri-information-line text-[#C4787A]" aria-hidden="true"></i>
              キャンペーンについて
            </h4>
            <ul className="text-xs sm:text-sm text-black/60 font-extralight space-y-1.5 sm:space-y-2 tracking-wide">
              <li className="flex items-start gap-2">
                <span className="text-[#C4787A] mt-0.5" aria-hidden="true">
                  •
                </span>
                <span>キャンペーン期間中に在庫切れとなった場合、購入ボタンは「予約」に切り替わります</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C4787A] mt-0.5" aria-hidden="true">
                  •
                </span>
                <span>予約特典の適用には、期間内の先入金が必要となります</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#C4787A] mt-0.5" aria-hidden="true">
                  •
                </span>
                <span>Amazonギフト券は商品発送後にメールにてお届けいたします</span>
              </li>
            </ul>
          </aside>
        </div>
      </section>
      ) : (
      <section className="py-16 sm:py-20 md:py-24 bg-gradient-to-br from-gray-50 to-gray-100" aria-labelledby="campaign-not-eligible">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-black/5 text-black/40 text-xs sm:text-sm font-light tracking-[0.1em] rounded-full mb-6">
            <i className="ri-information-line text-base" aria-hidden="true"></i>
            キャンペーンについて
          </div>
          <h2 id="campaign-not-eligible" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black/70 mb-4 tracking-[0.1em]">
            本製品はキャンペーン対象外です
          </h2>
          <p className="text-sm sm:text-base text-black/45 font-extralight leading-relaxed mb-8 tracking-wide max-w-xl mx-auto">
            MeVER Chair Core は今回の価格改定の対象外のため、新生活応援キャンペーンの特典は適用されません。Pro・Style モデルがキャンペーン対象となっております。
          </p>
          <Link
            to="/campaign"
            className="inline-flex items-center gap-2 px-6 sm:px-8 py-3 bg-black/8 text-black/60 text-sm font-light tracking-[0.1em] hover:bg-black/12 transition-colors whitespace-nowrap rounded-md cursor-pointer"
          >
            キャンペーン詳細を見る
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </section>
      )}

      {/* Other Models */}
      <section className="py-16 sm:py-20 md:py-24 bg-white" aria-labelledby="other-models-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <h2 id="other-models-heading" className="text-2xl sm:text-3xl md:text-4xl font-extralight text-black mb-10 sm:mb-12 md:mb-16 text-center tracking-[0.1em] sm:tracking-[0.15em]">
            他のモデル
          </h2>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8" role="list">
            {Object.entries(chairData)
              .filter(([key]) => key !== model)
              .map(([key, item]) => (
                <Link
                  key={key}
                  to={`/chair/${key}`}
                  className="group cursor-pointer"
                  role="listitem"
                  aria-label={`${item.name}の詳細ページへ`}
                >
                  <div className="aspect-square bg-gray-100 rounded-lg sm:rounded-xl overflow-hidden mb-3 sm:mb-4">
                    <img
                      src={item.image}
                      alt={`${item.name} - ${item.tagline}`}
                      className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      width="400"
                      height="400"
                    />
                  </div>
                  <h3 className="text-lg sm:text-xl font-light text-black mb-1.5 sm:mb-2 tracking-wide">{item.name}</h3>
                  <p className="text-sm sm:text-base text-black/60 font-extralight tracking-wide">{item.tagline}</p>
                </Link>
              ))}
          </div>
        </div>
      </section>

      <Footer />

      {showCoreModal && (
        <NewsletterModal
          title="MeVER Chair Core 発売情報"
          description="発売開始時にいち早くお知らせいたします。お名前とメールアドレスをご登録ください。"
          formType="core-notify"
          formUrl="https://readdy.ai/api/form/d69vup5bd17lru29lgng"
          onClose={() => setShowCoreModal(false)}
        />
      )}
    </div>
  );
}
